import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectPuttingCloseVirtualCardFromState,
  selectTopLevelErrorFromState,
} from 'src/app/CloseVirtualCardContent/redux/closeVirtualCardContent.selectors'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import SvgCloseCard from 'src/components/Svgs/SvgCloseCard'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { CONFIRM_CLOSE_CARD_TEXT, ConfirmCloseCardText } from 'src/constants'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { useQueryUser } from 'src/queries/users.queries'
import { putCloseVirtualCardAndCloseModal } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

interface CloseVirtualCardContentProps {
  virtualCardId: string
  virtualCard: VirtualCardSecureModel | undefined
}

const CloseVirtualCardContent: React.FC<CloseVirtualCardContentProps> = ({
  virtualCardId,
  virtualCard,
}): React.ReactElement => {
  const translate = useTranslate()

  const [confirmationText, setConfirmationText] = useState<string | undefined>()

  const dispatch = useDispatch()
  const history = useHistory()

  const closing = useSelector(selectPuttingCloseVirtualCardFromState)
  const error = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: sender } = useQueryUser({ userId: virtualCard?.cardholderId })
  const userId = useSelector(selectUserIdFromState)

  const isConfirmationFormValid =
    confirmationText === ConfirmCloseCardText[locale]

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (virtualCardId && isConfirmationFormValid) {
      dispatch(
        putCloseVirtualCardAndCloseModal(
          virtualCardId,
          virtualCard?.displayName || '',
        ),
      )
      analytics?.track('close vcn', {
        type_of_card: virtualCard?.cardTypeLabel.props.source,
        card_name: virtualCard?.displayName,
        credit_limit:
          virtualCard?.balanceCents !== undefined
            ? toDollars(virtualCard?.balanceCents)
            : undefined,
        active_until:
          virtualCard?.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
        add_attachments: virtualCard?.receiptAttachments?.length !== 0,
        add_notes: virtualCard?.notes !== undefined,
        is_card_creator: sender?.id === userId,
      })
    }
  }

  return (
    <Popup
      css={css`
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
    >
      {virtualCard !== undefined &&
      !virtualCard.isClosed &&
      (virtualCard.isActive || virtualCard.isInactive) ? (
        <ModalDialogForm
          formOnSubmit={handleFormSubmit}
          actionButtonLabel={
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <SvgCloseCard
                fill={styleConstants.colors.gray_lightest}
                css={css`
                  transform: scale(calc(16 / 24));
                `}
              />
              {translate('Confirm and Close Card')}
            </span>
          }
          actionButtonLoading={closing}
          actionButtonVariant="severe"
          cancelButtonLabel={translate('Cancel')}
          cancelButtonOnClick={(): void => {
            history.goBack()
          }}
          showCancelAsLink
          isFormValid={isConfirmationFormValid}
          error={error}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 32px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >
              {translate('Close Card')}
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                'Close ${cardName}? This will close your card permanently.',
                {
                  cardName: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {virtualCard?.displayName}
                    </span>
                  ),
                },
              )}
            </span>
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                'You can still view its transactions, but ${bolded}.',
                {
                  bolded: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {translate(
                        'you cannot reactivate this card in the future',
                      )}
                    </span>
                  ),
                },
              )}
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 40px;
                width: 100%;
              `}
            >
              <TextInputField
                allCaps
                design={TextInputFieldDesigns.New}
                name="remove-confirmation"
                fieldLabel={
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('Confirm by typing ${close} below:', {
                      close: (
                        <span style={{ color: `${styleConstants.colors.red}` }}>
                          {translate(`"${CONFIRM_CLOSE_CARD_TEXT}"`)}
                        </span>
                      ),
                    })}
                  </span>
                }
                placeholder={translateSingleString(
                  locale,
                  'Please type "CLOSE"',
                )}
                onChange={(value: string): void =>
                  setConfirmationText(value.toUpperCase())
                }
              />
            </span>
          </div>
        </ModalDialogForm>
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default CloseVirtualCardContent
